import React, { useState, useEffect, useRef, useCallback } from 'react'

import { returnShuffledCards } from './data'
import { loadHistory, saveHistory } from './history'

import { DownOutlined, UpOutlined, RedoOutlined } from '@ant-design/icons'
import { Button, Statistic, Typography, Row, Col, Result, Space, Table, Popconfirm } from 'antd'
const { Text, Title } = Typography

const FEEDBACK_COLOR = { success: 'var(--accent)', error: '#f87171', info: '#8b98a5' }

const returnNewGame = (game = {}) => {
  const cardIndex = 0
  const correctGuesses = 0
  const score = 0
  const highScore = game.highScore || 0
  const cards = returnShuffledCards()
  const gameId = `game_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`

  return { cardIndex, correctGuesses, score, highScore, cards, gameId }
}

const isPlaying = game => Boolean(game && game.cardIndex < game.cards.length - 1)

const StatsView = ({ game }) => {
  if (!game) return null
  const { score, correctGuesses, cards, cardIndex } = game

  return (
    <Row className='dvs-stats' justify='space-between'>
      <Col><Statistic title='Streak' value={score} /></Col>
      <Col><Statistic title='Correct' value={correctGuesses} /></Col>
      <Col><Statistic title='Cards left' value={cards.length - cardIndex - 1} /></Col>
    </Row>
  )
}

const BestStreakView = ({ game }) => {
  if (!game) return null
  return (
    <div className='dvs-best'>
      <span className='dvs-best-label'>Best streak</span>
      <span className='dvs-best-value'>{game.highScore}</span>
    </div>
  )
}

const GuessView = ({ game, onGuess }) => {
  const playing = isPlaying(game)
  if (!playing) return null

  return (
    <div className='dvs-guess'>
      <button className='dvs-guess-btn' onClick={() => onGuess('lower')}>
        <DownOutlined /><span>Lower</span>
      </button>
      <button className='dvs-guess-btn' onClick={() => onGuess('higher')}>
        <UpOutlined /><span>Higher</span>
      </button>
    </div>
  )
}

const CardView = ({ game }) => {
  const playing = isPlaying(game)
  if (!playing) return null

  const current = game.cards[game.cardIndex]

  return (
    <div className='dvs-cards'>
      <figure className='dvs-card'>
        <img src={current.src} alt={current.name} />
        <figcaption>Card {game.cardIndex + 1}</figcaption>
      </figure>
      <figure className='dvs-card'>
        <div className='dvs-card-back'><Text>?</Text></div>
        <figcaption>Next</figcaption>
      </figure>
    </div>
  )
}

const GameOverView = ({ game }) => {
  if (!game) return null
  const playing = isPlaying(game)
  if (playing) return null

  const { correctGuesses } = game

  return (
    <Result
      status='success'
      title={`${correctGuesses} correct ${correctGuesses === 1 ? 'guess' : 'guesses'}`}
    />
  )
}

const NewGameButton = ({ game, onNewGame }) => {
  const playing = isPlaying(game)

  return (
    <Button
      type='primary'
      size='large'
      shape='round'
      icon={<RedoOutlined />}
      onClick={onNewGame}
    >
      {playing ? 'Restart Game' : 'New Game'}
    </Button>
  )
}

const Feedback = ({ feedback }) => {
  if (!feedback) return null
  return (
    <div className='dvs-feedback' style={{ color: FEEDBACK_COLOR[feedback.type] }}>
      {feedback.content}
    </div>
  )
}

const friendlyDate = iso => {
  const d = new Date(iso)
  return `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}, ${d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`
}

const HistoryList = ({ history, onDelete }) => {
  if (!history || history.length === 0) return null

  const columns = [
    { title: 'Played', dataIndex: 'createdAt', render: friendlyDate },
    { title: 'Correct', dataIndex: 'correct', align: 'center' },
    {
      title: '',
      dataIndex: 'gameId',
      align: 'right',
      render: gameId => (
        <Popconfirm
          title='Delete this game?'
          okText='Yes'
          cancelText='No'
          onConfirm={() => onDelete(gameId)}
        >
          <Button type='link' size='small' style={{ color: '#8b98a5' }}>Delete</Button>
        </Popconfirm>
      )
    }
  ]

  return (
    <div className='dvs-history'>
      <Table
        title={() => <Title level={4} style={{ margin: 0, color: '#e6edf3' }}>GAME HISTORY</Title>}
        size='small'
        columns={columns}
        dataSource={history}
        rowKey='gameId'
        pagination={false}
        showHeader
      />
    </div>
  )
}

const Site = () => {
  const [game, setGame] = useState(() => returnNewGame())
  const [feedback, setFeedback] = useState()
  const [history, setHistory] = useState(() => loadHistory())
  const timer = useRef()

  const flash = (type, content) => {
    setFeedback({ type, content })
    clearTimeout(timer.current)
    timer.current = setTimeout(() => setFeedback(null), 1400)
  }

  useEffect(() => () => clearTimeout(timer.current), [])

  const recordHistory = useCallback(g => {
    if (!g || g.cardIndex === 0) return
    const list = loadHistory()
    if (list.some(h => h.gameId === g.gameId)) return
    list.unshift({
      gameId: g.gameId,
      createdAt: new Date().toISOString(),
      correct: g.correctGuesses,
      bestStreak: g.highScore,
      cards: g.cards.length
    })
    saveHistory(list)
    setHistory(list)
  }, [])

  useEffect(() => {
    if (game && !isPlaying(game)) recordHistory(game)
  }, [game, recordHistory])

  const onDeleteHistory = gameId => {
    const list = loadHistory().filter(h => h.gameId !== gameId)
    saveHistory(list)
    setHistory(list)
    // The gate's shim, not a library: `window.siteTrack` does not EXIST until the visitor
    // has said yes, so a refused visit has no way to send this event at all.
    window.siteTrack?.('delete_history', { label: gameId })
  }

  const onNewGame = () => {
    recordHistory(game)
    setGame(returnNewGame(game))
    flash('info', 'New game')
    window.siteTrack?.('new_game', { label: game.gameId })
  }

  const onGuess = guess => {
    const { cards, cardIndex } = game

    const nextCardIndex = game.cardIndex + 1

    const currentValue = cards[cardIndex].value
    const nextValue = cards[nextCardIndex].value

    const correct = Boolean((guess === 'higher' && nextValue > currentValue) || (guess === 'lower' && nextValue < currentValue))

    window.siteTrack?.(correct ? 'guess_correct' : 'guess_incorrect', { label: guess })

    if (correct) {
      flash('success', 'Right!')
    } else if (nextValue !== currentValue) { // not a tie then its wrong
      flash('error', 'Wrong')
    } else {
      flash('info', 'Tied')
    }

    const correctGuesses = correct ? game.correctGuesses + 1 : game.correctGuesses
    const score = correct ? game.score + 1 : 0
    const highScore = score > game.highScore ? score : game.highScore

    setGame({
      ...game,
      cardIndex: nextCardIndex,
      correctGuesses,
      score,
      highScore
    })

    if (nextCardIndex >= cards.length - 1) {
      window.siteTrack?.('game_completed', { label: game.gameId, value: correctGuesses })
    }
  }

  return (
    <Space direction='vertical' size='large' style={{ width: '100%' }}>
      <BestStreakView game={game} />
      <StatsView game={game} />
      <div className='dvs-play'>
        <CardView game={game} />
        <div className='dvs-guess-wrap'>
          <GuessView game={game} onGuess={onGuess} />
          <Feedback feedback={feedback} />
        </div>
      </div>
      <GameOverView game={game} />
      <div style={{ textAlign: 'center' }}>
        <NewGameButton game={game} onNewGame={onNewGame} />
      </div>
      <HistoryList history={history} onDelete={onDeleteHistory} />
    </Space>
  )
}

export default Site
